import { useState } from 'react';
import { SwalAlert } from '@/hooks/useSweetAlert';
import type { HiddenMessage } from '@/mocks/adminModeracionMessages';
import { hiddenMessages as initialHidden } from '@/mocks/adminModeracionMessages';

export default function HiddenMessagesPanel() {
  const [messages, setMessages] = useState<HiddenMessage[]>(initialHidden);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const filtered = messages.filter((m) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return m.username.toLowerCase().includes(q) || m.messagePreview.toLowerCase().includes(q) || m.reason.toLowerCase().includes(q);
  });

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('es-CR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  const handleRestore = (msg: HiddenMessage) => {
    SwalAlert.confirm('¿Restaurar mensaje?', `El mensaje de ${msg.username} volverá a ser visible en el chat público.`).then((result) => {
      if (result.isConfirmed) {
        setMessages((prev) => prev.filter((m) => m.id !== msg.id));
        SwalAlert.toast('Mensaje restaurado correctamente', 'success');
      }
    });
  };

  const handleDelete = (msg: HiddenMessage) => {
    SwalAlert.confirm('¿Eliminar mensaje permanentemente?', `El mensaje de ${msg.username} será eliminado para siempre. Esta acción no se puede deshacer.`).then((result) => {
      if (result.isConfirmed) {
        setMessages((prev) => prev.filter((m) => m.id !== msg.id));
        SwalAlert.toast('Mensaje eliminado permanentemente', 'success');
      }
    });
  };

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {[
          { label: 'Mensajes ocultos', value: messages.length, icon: 'ri-eye-off-line', color: 'text-foreground-500' },
          { label: 'Auto-detectados', value: messages.filter((m) => m.autoDetected).length, icon: 'ri-robot-2-line', color: 'text-violet-500' },
          { label: 'Manuales', value: messages.filter((m) => !m.autoDetected).length, icon: 'ri-user-line', color: 'text-amber-500' },
        ].map((s) => (
          <div key={s.label} className="bg-background-50 border border-background-200 rounded-xl p-3.5">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center">
                <i className={`${s.icon} ${s.color} text-sm`}></i>
              </div>
              <span className="text-[10px] text-foreground-400 font-medium">{s.label}</span>
            </div>
            <p className="text-lg font-bold text-foreground-900">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por usuario o contenido..."
          className="w-full pl-8 pr-8 py-2 rounded-xl bg-background-50 text-foreground-800 text-xs border border-background-200 focus:border-secondary-300 focus:ring-1 focus:ring-secondary-200 outline-none transition-all placeholder:text-foreground-400"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-foreground-300 flex items-center justify-center cursor-pointer hover:bg-foreground-400 transition-colors">
            <i className="ri-close-line text-background-50 text-[8px]"></i>
          </button>
        )}
      </div>

      {/* Message cards */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center py-12 gap-2 bg-background-50 border border-background-200 rounded-xl">
          <div className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-eye-off-line text-foreground-300 text-lg"></i>
          </div>
          <p className="text-xs text-foreground-400 font-medium">Sin mensajes ocultos</p>
          <p className="text-[10px] text-foreground-400">No se encontraron mensajes con esos filtros</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((msg) => (
            <div
              key={msg.id}
              className="bg-background-50 border border-background-200 rounded-xl overflow-hidden transition-all"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${msg.autoDetected ? 'bg-violet-100 text-violet-600' : 'bg-amber-100 text-amber-600'}`}>
                    {msg.avatarLetter}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-foreground-900">{msg.username}</span>
                      <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${msg.autoDetected ? 'bg-violet-100 text-violet-600' : 'bg-amber-100 text-amber-600'}`}>
                        <i className={`${msg.autoDetected ? 'ri-robot-2-line' : 'ri-user-line'} mr-0.5`}></i>
                        {msg.autoDetected ? 'Auto' : 'Manual'}
                      </span>
                    </div>
                    <p className="text-[10px] text-foreground-500">{formatDate(msg.hiddenAt)} por {msg.hiddenBy}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => setExpanded(expanded === msg.id ? null : msg.id)}
                    className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                    title="Ver mensaje completo"
                  >
                    <i className={`${expanded === msg.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500 text-xs`}></i>
                  </button>
                  <button
                    onClick={() => handleRestore(msg)}
                    className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center cursor-pointer hover:bg-emerald-200 transition-colors"
                    title="Restaurar mensaje"
                  >
                    <i className="ri-eye-line text-emerald-600 text-xs"></i>
                  </button>
                  <button
                    onClick={() => handleDelete(msg)}
                    className="w-7 h-7 rounded-lg bg-red-100 flex items-center justify-center cursor-pointer hover:bg-red-200 transition-colors"
                    title="Eliminar permanentemente"
                  >
                    <i className="ri-delete-bin-line text-red-600 text-xs"></i>
                  </button>
                </div>
              </div>

              {/* Reason */}
              <div className="px-4 pb-3">
                <div className="bg-background-100 rounded-lg px-3 py-2">
                  <p className="text-[10px] text-foreground-500">
                    <i className="ri-information-line text-foreground-400 mr-1"></i>
                    {msg.reason}
                  </p>
                </div>
              </div>

              {/* Expanded content */}
              {expanded === msg.id && (
                <div className="px-4 pb-4 border-t border-background-100 pt-3">
                  <p className="text-[10px] font-semibold text-foreground-400 mb-1.5">Vista previa</p>
                  <p className="text-xs text-foreground-700 bg-background-100 rounded-lg px-3 py-2.5 leading-relaxed italic">
                    &ldquo;{msg.messagePreview}&rdquo;
                  </p>
                  <p className="text-[10px] font-semibold text-foreground-400 mt-3 mb-1.5">Mensaje completo</p>
                  <p className="text-xs text-foreground-800 bg-background-100 rounded-lg px-3 py-2.5 leading-relaxed">
                    {msg.fullMessage}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}